/**
 * Simple polling service for checking new messages.
 * Uses setTimeout-based loop to prevent overlapping requests,
 * with exponential backoff on errors (especially 429s).
 * Will be replaced by WebSocket/SSE in Phase 5.
 */
export class Poller {
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private baseIntervalMs: number;
  private currentIntervalMs: number;
  private callback: () => Promise<void>;
  private isRunning = false;
  private isPolling = false;

  private static readonly MAX_BACKOFF_MS = 120_000; // 2 minutes
  private static readonly BACKOFF_MULTIPLIER = 2;
  private consecutiveErrors = 0;

  constructor(callback: () => Promise<void>, intervalMs = 5000) {
    this.callback = callback;
    this.baseIntervalMs = intervalMs;
    this.currentIntervalMs = intervalMs;
  }

  start(): void {
    if (this.isRunning) return;
    this.isRunning = true;

    // Execute immediately, then schedule next
    this.tick();
  }

  /** Like start(), but waits one interval before the first poll (when the caller already has a fresh value). */
  startDelayed(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.scheduleNext();
  }

  stop(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.isRunning = false;
  }

  /** Adjust base polling frequency (e.g., slower when tab is hidden) */
  setInterval(ms: number): void {
    this.baseIntervalMs = ms;
    // Only reset current interval if we're not in a backoff state
    if (this.consecutiveErrors === 0) {
      this.currentIntervalMs = ms;
    }
    if (this.isRunning) {
      // Reschedule with the new interval
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
      }
      this.scheduleNext();
    }
  }

  /** Request an immediate poll (e.g., on tab focus). No-op if already polling. */
  pollNow(): void {
    if (!this.isRunning || this.isPolling) return;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.tick();
  }

  private scheduleNext(): void {
    if (!this.isRunning) return;
    this.timeoutId = setTimeout(() => this.tick(), this.currentIntervalMs);
  }

  private async tick(): Promise<void> {
    if (this.isPolling) return; // Guard against overlapping calls
    this.isPolling = true;

    try {
      await this.callback();
      // Success — reset backoff
      this.consecutiveErrors = 0;
      this.currentIntervalMs = this.baseIntervalMs;
    } catch (err) {
      this.consecutiveErrors++;
      // Exponential backoff: base * 2^errors, capped at MAX_BACKOFF_MS
      this.currentIntervalMs = Math.min(
        this.baseIntervalMs * Math.pow(Poller.BACKOFF_MULTIPLIER, this.consecutiveErrors),
        Poller.MAX_BACKOFF_MS,
      );
      console.debug(
        `[envy-chat] Poll error (attempt ${this.consecutiveErrors}, next in ${this.currentIntervalMs}ms):`,
        err,
      );
    } finally {
      this.isPolling = false;
      this.scheduleNext();
    }
  }
}
